import { AcceptInviteCommand } from '../../application/dto/auth/AcceptInviteCommand';
import { ChangePasswordCommand } from '../../application/dto/auth/ChangePasswordCommand';
import { ForgotPasswordCommand } from '../../application/dto/auth/ForgotPasswordCommand';
import { InviteUserCommand } from '../../application/dto/auth/InviteUserCommand';
import { LoginCommand } from '../../application/dto/auth/LoginCommand';
import { LoginResult } from '../../application/dto/auth/LoginResult';
import { PasswordResetResult } from '../../application/dto/auth/PasswordResetResult';
import { ResetPasswordCommand } from '../../application/dto/auth/ResetPasswordCommand';
import { ApiClient } from '../api/ApiClient';
import { API_ENDPOINTS } from '../api/config';

/**
 * MineMinerAuthService — auth surface for the carved MineMiner SPA.
 *
 * sprint-104 (epic-013 Sprint B, step 2d / S5). The carved `MineMiner.API`
 * host originally exposed only `POST /api/mineminer/auth/login`. sprint-107
 * S1.5 (epic-013 Sprint D — Stream A item A2) added forgot-password +
 * reset-password endpoints.
 *
 * sprint-130 S4 — sliding session. Login now stashes the refresh token too, and
 * `refresh()` exchanges the stored pair for a rotated one (the ApiClient does the
 * actual rotation + storage so the reactive 401-retry path and this proactive
 * path share one implementation).
 */
export class AuthService {
  constructor(private readonly apiClient: ApiClient) {}

  /**
   * Sign in a MineMiner rep against the carved host.
   */
  async login(command: LoginCommand): Promise<LoginResult> {
    const result = await this.apiClient.post<LoginResult>(
      API_ENDPOINTS.AUTH.LOGIN,
      command,
    );

    if (result.succeeded && result.accessToken) {
      this.apiClient.setAuthToken(result.accessToken);
      if (result.refreshToken) {
        this.apiClient.setRefreshToken(result.refreshToken);
      }
    }

    return result;
  }

  /**
   * sprint-130 S4 — proactively slide the session by rotating the token pair.
   * Resolves true when new tokens were stored, false when the refresh failed
   * (the caller then lets the rep re-authenticate). Safe to call concurrently —
   * the ApiClient de-duplicates in-flight refreshes.
   */
  refresh(): Promise<boolean> {
    return this.apiClient.refreshSession();
  }

  /**
   * Request a password-reset email. The carved host always returns 200
   * (email-enumeration protection); the resolved `PasswordResetResult.message`
   * is the user-facing copy to render on success.
   */
  async forgotPassword(command: ForgotPasswordCommand): Promise<PasswordResetResult> {
    return this.apiClient.post<PasswordResetResult>(
      API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      command,
    );
  }

  /**
   * Complete a password reset using the token from the forgot-password email.
   * Returns `{ succeeded: true }` on success or `{ succeeded: false, error,
   * errors }` on validation/token failure (HTTP 400 raises the ApiError that
   * the caller catches; here we re-shape the JSON body if present).
   */
  async resetPassword(command: ResetPasswordCommand): Promise<PasswordResetResult> {
    return this.apiClient.post<PasswordResetResult>(
      API_ENDPOINTS.AUTH.RESET_PASSWORD,
      command,
    );
  }

  /**
   * sprint-108 S1 — admin sends an invite to a new rep / admin. Endpoint is
   * `[Authorize(Roles = MineMinerAdmin)]`; the ApiClient already attaches the
   * bearer token, so callers just need to be signed in as an admin. Response
   * shape mirrors `PasswordResetResult` (succeeded + message on 200, error +
   * errors[] on 400 — e.g. "user already exists").
   */
  async inviteUser(command: InviteUserCommand): Promise<PasswordResetResult> {
    return this.apiClient.post<PasswordResetResult>(
      API_ENDPOINTS.AUTH.INVITE_USER,
      command,
    );
  }

  /**
   * sprint-108 S1 — rep redeems the tokenized link from their invite email.
   * Anonymous endpoint, rate-limited (5/hr/IP). Response mirrors
   * `PasswordResetResult`.
   */
  async acceptInvite(command: AcceptInviteCommand): Promise<PasswordResetResult> {
    return this.apiClient.post<PasswordResetResult>(
      API_ENDPOINTS.AUTH.ACCEPT_INVITE,
      command,
    );
  }

  /**
   * sprint-108 S2 — signed-in rep rotates their own password without going
   * through forgot/reset. Auth-gated; the ApiClient already attaches the
   * bearer token. UserId is read server-side from the JWT principal claim
   * (NOT carried in the request body). Response mirrors `PasswordResetResult`
   * (succeeded + message on 200, error + errors[] on 400 — e.g. wrong current
   * password or policy violation).
   */
  async changePassword(command: ChangePasswordCommand): Promise<PasswordResetResult> {
    return this.apiClient.post<PasswordResetResult>(
      API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
      command,
    );
  }

  /**
   * Clear the locally stored access token. The MineMiner host has no
   * server-side logout endpoint; the token is bearer-only and short-lived.
   * Returns a resolved Promise to preserve the existing async caller contract.
   */
  logout(): Promise<void> {
    this.apiClient.clearAuthToken();
    return Promise.resolve();
  }

  /**
   * Returns true when a bearer token is present in localStorage.
   */
  isAuthenticated(): boolean {
    const token =
      typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    return Boolean(token);
  }
}
